import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { kgToBoxes, BOX_KG } from '../../utils/packaging';

interface LineItem {
  productId: string;
  description: string;
  grade: string;
  quantityKg: string;
  pricePerKg: string;
}

interface Buyer { id: string; companyName: string; }
interface Coffee { id: string; title: string; gradeBreakdown: Record<string, number>; gradePricing: Record<string, number>; }

const emptyLineItem: LineItem = { productId: '', description: '', grade: '', quantityKg: '', pricePerKg: '' };

export const OfferForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    buyerId: '', currency: 'USD', incoterms: 'FOB Mombasa', shippingMonth: '', notes: '',
  });
  const [lineItems, setLineItems] = useState<LineItem[]>([{ ...emptyLineItem }]);
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [coffees, setCoffees] = useState<Coffee[]>([]);
  const [status, setStatus] = useState('draft');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/admin/buyers', { credentials: 'include' })
      .then(r => r.json())
      .then(data => setBuyers(Array.isArray(data) ? data : []))
      .catch(() => {});

    fetch('/api/coffees?includeArchived=true', { credentials: 'include' })
      .then(r => r.json())
      .then(data => setCoffees(Array.isArray(data) ? data : []))
      .catch(() => {});

    if (isEdit) {
      fetch(`/api/admin/offers?id=${id}`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          setForm({
            buyerId: data.buyerId || '',
            currency: data.currency || 'USD',
            incoterms: data.incoterms || 'FOB Mombasa',
            shippingMonth: data.shippingMonth || '',
            notes: data.notes || '',
          });
          setStatus(data.status || 'draft');
          if (data.lineItems?.length) {
            setLineItems(data.lineItems.map((li: any) => ({
              productId: li.productId || '',
              description: li.description || '',
              grade: li.grade,
              quantityKg: li.quantityKg,
              pricePerKg: li.pricePerKg,
            })));
          }
        });
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const updateLineItem = (i: number, field: keyof LineItem, value: string) => {
    setLineItems(prev => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      return next;
    });
  };

  const selectProductForLine = (i: number, productId: string) => {
    const coffee = coffees.find(c => c.id === productId);
    setLineItems(prev => {
      const next = [...prev];
      next[i] = { ...next[i], productId, description: coffee ? '' : next[i].description, grade: '', pricePerKg: '', quantityKg: '' };
      return next;
    });
  };

  const selectGradeForLine = (i: number, grade: string) => {
    setLineItems(prev => {
      const next = [...prev];
      const coffee = coffees.find(c => c.id === next[i].productId);
      const price = coffee?.gradePricing?.[grade];
      next[i] = { ...next[i], grade, pricePerKg: price != null ? String(price) : next[i].pricePerKg };
      return next;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...emptyLineItem }]);
  const removeLine = (i: number) => setLineItems(prev => prev.filter((_, j) => j !== i));

  const totalQuantityKg = lineItems.reduce((sum, li) => sum + (parseFloat(li.quantityKg) || 0), 0);
  const totalValue = lineItems.reduce((sum, li) => sum + (parseFloat(li.quantityKg) || 0) * (parseFloat(li.pricePerKg) || 0), 0);
  const totalBoxes = lineItems.reduce((sum, li) => sum + kgToBoxes(parseFloat(li.quantityKg) || 0), 0);

  const fmtCurrency = (amount: number) => {
    const symbols: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', KES: 'KES ' };
    const sym = symbols[form.currency] || form.currency + ' ';
    return `${sym}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const validItems = lineItems.filter(li => (li.productId || li.description) && li.grade && li.quantityKg && li.pricePerKg);
    if (validItems.length === 0) {
      setError('Add at least one line item with a lot (or description), grade, quantity, and price.');
      setSaving(false);
      return;
    }

    const body = isEdit ? { id, ...form, lineItems: validItems } : { ...form, lineItems: validItems };

    try {
      const res = await fetch('/api/admin/offers', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to save');
      navigate('/admin/offers');
    } catch (err: any) {
      setError(err.message);
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 border border-[#E8E6E2] rounded-xl bg-[#FAFAF8] text-[#1A1A1A] text-sm focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all placeholder-[#C4C4C4]";
  const labelClass = "block text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold mb-1.5";

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">{isEdit ? 'Edit Offer' : 'New Offer'}</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">Aggregated lot &amp; grade offer, emailed to a buyer</p>
        </div>
        <button onClick={() => navigate('/admin/offers')} className="text-sm text-[#A3A3A3] hover:text-[#1A1A1A] transition-colors font-medium">
          Back to offers
        </button>
      </div>

      {isEdit && (status === 'sent' || status === 'declined') && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-100 rounded-xl text-amber-700 text-sm font-medium">
          This offer has already been {status}. Update the terms below and save, then use "{status === 'declined' ? 'Amend & Resend' : 'Resend'}" from the offers list to send the buyer the new price — saving here alone won't notify them.
        </div>
      )}
      {isEdit && status === 'accepted' && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-100 rounded-xl text-amber-700 text-sm font-medium">
          This offer was accepted and a contract was already created from it. Editing it here won't change that contract or notify the buyer.
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm font-medium">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Offer Details</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Buyer</label>
              <select name="buyerId" value={form.buyerId} onChange={handleChange} className={inputClass}>
                <option value="">Select buyer...</option>
                {buyers.map(b => <option key={b.id} value={b.id}>{b.companyName}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Currency</label>
              <select name="currency" value={form.currency} onChange={handleChange} className={inputClass}>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (&euro;)</option>
                <option value="GBP">GBP (&pound;)</option>
                <option value="KES">KES</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Incoterms</label>
              <select name="incoterms" value={form.incoterms} onChange={handleChange} className={inputClass}>
                <option value="FOB Mombasa">FOB Mombasa</option>
                <option value="CIF">CIF</option>
                <option value="CFR">CFR</option>
                <option value="EXW">EXW</option>
                <option value="FCA">FCA</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Shipping Month</label>
              <input name="shippingMonth" value={form.shippingMonth} onChange={handleChange} placeholder="e.g. April 2026" className={inputClass} />
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Lots &amp; Grades</h2>

          <div className="flex gap-3 items-end mb-2 px-1">
            <div className="flex-1"><span className={labelClass}>Lot</span></div>
            <div className="w-24"><span className={labelClass}>Grade</span></div>
            <div className="w-28"><span className={labelClass}>Qty (kg)</span></div>
            <div className="w-16"><span className={labelClass}>Boxes</span></div>
            <div className="w-28"><span className={labelClass}>Price / kg</span></div>
            <div className="w-28 text-right"><span className={labelClass}>Line Total</span></div>
            <div className="w-8" />
          </div>

          <div className="space-y-2">
            {lineItems.map((li, i) => {
              const coffee = coffees.find(c => c.id === li.productId);
              const grades = coffee ? Object.keys(coffee.gradeBreakdown || {}).filter(g => (coffee.gradeBreakdown[g] || 0) > 0) : [];
              const lineTotal = (parseFloat(li.quantityKg) || 0) * (parseFloat(li.pricePerKg) || 0);
              const available = coffee && li.grade ? coffee.gradeBreakdown?.[li.grade] : undefined;
              return (
                <div key={i} className="flex gap-3 items-center p-3 bg-[#FAFAF8] rounded-xl border border-[#E8E6E2]/40">
                  <div className="flex-1 space-y-1.5">
                    <select value={li.productId} onChange={e => selectProductForLine(i, e.target.value)} className={inputClass}>
                      <option value="">— generic lot —</option>
                      {coffees.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                    {!coffee && (
                      <input
                        value={li.description}
                        onChange={e => updateLineItem(i, 'description', e.target.value)}
                        placeholder="e.g. Classic Peaberry"
                        className={inputClass}
                      />
                    )}
                  </div>
                  <div className="w-24">
                    {coffee ? (
                      <select value={li.grade} onChange={e => selectGradeForLine(i, e.target.value)} className={inputClass}>
                        <option value="">Grade</option>
                        {grades.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    ) : (
                      <input value={li.grade} onChange={e => updateLineItem(i, 'grade', e.target.value)} placeholder="PB" className={inputClass} />
                    )}
                  </div>
                  <div className="w-28">
                    <input type="number" step="0.01" value={li.quantityKg} onChange={e => updateLineItem(i, 'quantityKg', e.target.value)} placeholder="0.00" className={inputClass} />
                    {available != null && <p className="text-[10px] text-[#A3A3A3] mt-1">{available}kg avail.</p>}
                  </div>
                  <div className="w-16 text-center">
                    <p className="text-sm text-[#6B6B6B] tabular-nums">{kgToBoxes(parseFloat(li.quantityKg) || 0)}</p>
                  </div>
                  <div className="w-28">
                    <input type="number" step="0.01" value={li.pricePerKg} onChange={e => updateLineItem(i, 'pricePerKg', e.target.value)} placeholder="0.00" className={inputClass} />
                  </div>
                  <div className="w-28 text-right">
                    <p className="text-sm font-semibold text-[#1A1A1A] tabular-nums">{fmtCurrency(lineTotal)}</p>
                  </div>
                  <button type="button" onClick={() => removeLine(i)} className="w-8 h-8 flex items-center justify-center text-[#C4C4C4] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" disabled={lineItems.length === 1}>
                    &times;
                  </button>
                </div>
              );
            })}
          </div>

          <button type="button" onClick={addLine} className="mt-4 px-4 py-2.5 text-sm text-[#6B6B6B] hover:text-[#1A1A1A] border border-dashed border-[#E8E6E2] hover:border-[#A3A3A3] rounded-xl transition-all w-full">
            + Add line item
          </button>
          <p className="mt-2 text-[11px] text-[#A3A3A3]">Boxes assume {BOX_KG}kg vacuum-packed boxes (two 15kg bricks each) — this year's standard pack.</p>

          <div className="mt-6 pt-5 border-t border-[#E8E6E2]/60 flex justify-end">
            <div className="w-72 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#A3A3A3]">Total Quantity</span>
                <span className="text-[#1A1A1A] tabular-nums font-medium">{totalQuantityKg.toLocaleString('en-US')} kg</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#A3A3A3]">Total Boxes</span>
                <span className="text-[#1A1A1A] tabular-nums font-medium">{totalBoxes.toLocaleString('en-US')}</span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-3 border-t-2 border-[#1A1A1A]">
                <span className="text-[#1A1A1A]">Total Value</span>
                <span className="text-[#1A1A1A] tabular-nums">{fmtCurrency(totalValue)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Notes</h2>
          <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Anything else to include in the offer email..." className={inputClass} />
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button type="submit" disabled={saving} className="px-8 py-3 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all disabled:opacity-40 shadow-sm shadow-black/10">
            {saving ? 'Saving...' : isEdit ? 'Update Offer' : 'Save Draft'}
          </button>
          <button type="button" onClick={() => navigate('/admin/offers')} className="px-6 py-3 border border-[#E8E6E2] rounded-xl text-sm text-[#6B6B6B] hover:bg-[#F5F3F0] hover:border-[#E0DDD8] transition-all font-medium">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
